import { merge } from 'lodash-es'

import { FormMetadata } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'

import attestationFormMetadata from './attestationFormMetadata'
import getApproachingSpecialityCareMetadata from './getApproachingSpecialityCareMetadata'
import getPreAuthSpecialityCareMetadata from './getPreAuthSpecialityCareMetadata'
import { specialityCareAttestationStatuses } from '../../constants/appConstants'
import { prepopulateAttestation } from '../../utils/utils'

type SpecialtyCareAttestationParams = {
  specialtyCareAttestationStatus: string | undefined
  companyLabel: string | undefined
  firstName: string | undefined
  fullName: string | undefined
  pastHealthPlanPayments: any[] | undefined
}

export const getSpecialtyCareAttestationExtension = ({
  specialtyCareAttestationStatus,
  companyLabel,
  firstName,
  fullName,
  pastHealthPlanPayments,
}: SpecialtyCareAttestationParams): {
  metadata: FormMetadata
  initialValues: Record<string, any>
} | null => {
  const { APPROACHING_ATTESTATION, ATTESTATION_PRIOR_AUTHORIZATION, ATTESTATION_NEEDED } =
    specialityCareAttestationStatuses

  switch (specialtyCareAttestationStatus) {
    case APPROACHING_ATTESTATION:
      return {
        metadata: getApproachingSpecialityCareMetadata(companyLabel, fullName).meta_data as FormMetadata,
        initialValues: {},
      }
    case ATTESTATION_NEEDED:
      return {
        metadata: attestationFormMetadata.meta_data as FormMetadata,
        initialValues: prepopulateAttestation(pastHealthPlanPayments) ?? {},
      }
    case ATTESTATION_PRIOR_AUTHORIZATION:
      return {
        metadata: getPreAuthSpecialityCareMetadata(companyLabel, firstName, fullName).meta_data as FormMetadata,
        initialValues: {},
      }
    default:
      return null
  }
}

export const applySpecialtyCareAttestation = (
  metadata: FormMetadata,
  params: SpecialtyCareAttestationParams,
): FormMetadata => {
  const extension = getSpecialtyCareAttestationExtension(params)
  if (!extension) return metadata

  const { metadata: extensionMeta, initialValues } = extension
  const extendedProperties = { ...metadata.schema.properties, ...extensionMeta.schema.properties }
  const extendedUiSchema = { ...metadata.uiSchema, ...extensionMeta.uiSchema }
  const baseOrder = metadata.uiSchema['ui:order'] ?? []
  extendedUiSchema['ui:order'] = [
    ...baseOrder,
    ...(extensionMeta.uiSchema['ui:order'] ?? []).filter((key: string) => !baseOrder.includes(key)),
  ]
  const extendedInitialValues = merge({}, metadata.initialValues ?? {}, initialValues)

  return {
    ...metadata,
    schema: { ...metadata.schema, properties: extendedProperties },
    uiSchema: extendedUiSchema,
    initialValues: extendedInitialValues,
  }
}
